// Provider-execution child contract (SM-02.04).
//
// StripePendingMutationV1 documents the mutation Lambda's pre-token-wait
// return value. The ACTUAL inter-state payload that resumes the child
// state machine after the token wait is whatever SendTaskSuccess
// delivers (= StripeWebhookResumeV1), not StripePendingMutationV1.
//
// Error codes are modeled as strings here; Issue #2 will introduce
// StripeRetryableError / StripeNonRetryableError classes in node-backend
// that emit these codes.

import { z } from "zod/v4";

import type { Stripe } from "stripe";

import { StripeProductVersionList } from "../../../schemas/stripe";
import { BridgeMutationRecoveryRefV1Schema } from "../bridge-mutation-receipt";
import { BillingCadenceList } from "./catalog";
import { BillingPlanIntentRefV1Schema } from "./plan-intent-ref";
import {
  BillingAdminPhaseSchema,
  BillingAdminSettingsSchema,
  BillingModuleTrialCascadeBranchList,
  BillingModuleTrialGrantTriggerList,
  StripeDesiredDiscountV1Schema,
} from "./payloads";
import { BillingRuntimeCheckpointV1Schema } from "./runtime";
import { billingStateAssertionError } from "./state-assertion-error";

export const CorrelationIdsSchema = z.object({
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  executionId: z.string().min(1),
});

export type CorrelationIds = z.infer<typeof CorrelationIdsSchema>;

export const IdempotencyKeysSchema = z.object({
  stripeIdempotencyKey: z.string().min(1),
});

export type IdempotencyKeys = z.infer<typeof IdempotencyKeysSchema>;

export const StripeSubscriptionRefsSchema = z.object({
  stripeSubscriptionId: z.string().min(1).optional(),
  stripeCustomerId: z.string().min(1).optional(),
  stripeScheduleId: z.string().min(1).optional(),
  stripePriceIds: z.array(z.string().min(1)).optional(),
});

export type StripeSubscriptionRefs = z.infer<
  typeof StripeSubscriptionRefsSchema
>;

export const ExistingSubscriptionRefsSchema = StripeSubscriptionRefsSchema;

export type ExistingSubscriptionRefs = z.infer<
  typeof ExistingSubscriptionRefsSchema
>;

export const ShopifyExistingRefsSchema = z.object({
  shopifyAppSubscriptionId: z.string().min(1).optional(),
  shopifyAppSubscriptionLineItemId: z.string().min(1).optional(),
  shopifyChargeId: z.string().min(1).optional(),
});

export type ShopifyExistingRefs = z.infer<typeof ShopifyExistingRefsSchema>;

export const ShopifyBillingRefsSchema = z.object({
  shopifyAppSubscriptionId: z.string().min(1).optional(),
  shopifyAppSubscriptionLineItemId: z.string().min(1).optional(),
  shopifyChargeId: z.string().min(1).optional(),
  billingPlanStatus: z.string().min(1).optional(),
  billingRedirectUrl: z.string().min(1).optional(),
});

export type ShopifyBillingRefs = z.infer<typeof ShopifyBillingRefsSchema>;

// Resolved per-module trial (ADR 0013). `trialEnd` is unix seconds; `version`
// scopes the reusable Trial Offer lookup. Shared by the execution inputs and the
// add item-op so the resolved-trial contract has one source of truth.
export const ResolvedModuleTrialSchema = z.object({
  trialEnd: z.number().int().positive(),
  // When present, the grant lane sends `trialEnd` day-anchored to the next
  // UTC midnight rather than verbatim (`trialEnd` is the resolution instant
  // plus this many days, so that equals midnight-anchoring the resolution
  // instant). The anchored value is a pure function of this payload, which
  // keeps attach retries byte-identical under Stripe's per-item idempotency
  // key. Absent on absolute-end grants (the admin asked for a specific
  // calendar end: honor it or reject) and on plans resolved before this
  // field existed.
  trialDurationDays: z.number().int().positive().optional(),
  version: z.string().min(1),
  // The trialed module's price id, resolved from the grant marker. The grant
  // lane binds the Trial Offer to exactly the add op carrying this price id.
  priceId: z.string().min(1),
  // Grant attribution carried verbatim from the command's marker so the
  // grant lane can report it without re-reading the command.
  trigger: z.enum(BillingModuleTrialGrantTriggerList).optional(),
  branch: z.enum(BillingModuleTrialCascadeBranchList).optional(),
});

export type ResolvedModuleTrial = z.infer<typeof ResolvedModuleTrialSchema>;

export const StripeProviderExecutionInputsSchema = z.object({
  targetPriceIds: z.array(z.string().min(1)).optional(),
  // The resolved target's catalog version, carried so the Shopify bridge can
  // stamp the subscription link it writes — the link-write layer has no
  // default (a silent fallback once mislabeled a modular link as v3).
  targetCatalogVersion: z.enum(StripeProductVersionList).optional(),
  // The order-volume quantity for a modular Shopify bridge — self-serve from the
  // org's measured order metric, admin from the drawer's phase tier. Absent on
  // legacy plans and on payloads dispatched before it existed, which bill the
  // bridge at quantity 1.
  shopifyBridgeQuantity: z.number().int().nonnegative().optional(),
  cadence: z.enum(BillingCadenceList).optional(),
  shopifyUsageCapAmount: z.number().positive().optional(),
  // Lazy-cap decision for a mutate-existing Shopify command (ADR 0020).
  // "no_raise": the new monthly total fits under the live cap with headroom,
  // so no merchant-facing cap mutation is issued — shopifyUsageCapAmount then
  // carries the existing cap. "raise": shopifyUsageCapAmount carries the
  // generously sized ($1k-ceiling) new cap. Absent on creates and on payloads
  // dispatched before the policy existed.
  shopifyUsageCap: z
    .object({
      mode: z.enum(["no_raise", "raise"]),
      cappedAmount: z.number().positive(),
    })
    .optional(),
  // Deprecated single coupon; superseded by desiredDiscounts on the new path.
  couponId: z.string().min(1).nullable().optional(),
  // Ordered desired Discount Stack composed by policy-resolution for the immediate
  // (non-phase) mutation: Bundle Discount at #0 ({coupon}), preserved Operator
  // Coupons by discount id ({discount}), newly added ones by {coupon}.
  // undefined = no discount change; [] = clear all discounts.
  desiredDiscounts: z.array(StripeDesiredDiscountV1Schema).optional(),
  trialEndAt: z.string().optional(),
  trialEndNow: z.boolean().optional(),
  // When set, each added item is attached as a Trial Offer ending at its own
  // `trialEnd` rather than billed immediately.
  moduleTrials: ResolvedModuleTrialSchema.array().min(1).optional(),
  prorationBehavior: z
    .enum(["create_prorations", "none", "always_invoice"])
    .optional(),
  phases: z.array(BillingAdminPhaseSchema).min(1).optional(),
  customerId: z.string().min(1).optional(),
  agencyId: z.string().min(1).nullish(),
  settings: BillingAdminSettingsSchema.optional(),
  subscriptionLinkIds: z.array(z.string().min(1)).min(1).optional(),
});

export type StripeProviderExecutionInputs = z.infer<
  typeof StripeProviderExecutionInputsSchema
>;

export const StripeProviderMutationItemOperationV1Schema = z.discriminatedUnion(
  "op",
  [
    z
      .object({
        op: z.literal("add"),
        priceId: z.string().min(1),
        quantity: z.number().int().nonnegative().optional(),
        // Marks this add as a per-module trial grant (ADR 0013). When present,
        // provider-execution migrates the sub to flexible (if classic) and
        // attaches the price's reusable Trial Offer instead of adding the priced
        // item directly.
        moduleTrial: ResolvedModuleTrialSchema.optional(),
      })
      .strict(),
    z
      .object({
        op: z.literal("delete"),
        subscriptionItemId: z.string().min(1),
        priceId: z.string().min(1),
      })
      .strict(),
  ]
);

export type StripeProviderMutationItemOperationV1 = z.infer<
  typeof StripeProviderMutationItemOperationV1Schema
>;

export const StripeProrationBehaviorSchema = z.enum([
  "create_prorations",
  "none",
  "always_invoice",
]);

export type StripeProrationBehavior = z.infer<
  typeof StripeProrationBehaviorSchema
>;

// Single source of truth for the proration mode applied when a Stripe mutation
// does not specify one. Provider execution charges with this mode, and the
// frontend proration estimate keys its display semantics off the same value, so
// the two cannot drift.
export const DEFAULT_STRIPE_PRORATION_BEHAVIOR =
  "always_invoice" satisfies StripeProrationBehavior;

export const StripeProviderMutationV1Schema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("subscription.create"),
    customerId: z.string().min(1),
    targetPriceIds: z.array(z.string().min(1)).min(1),
    quantity: z.number().int().nonnegative().optional(),
    // Deprecated single coupon; superseded by desiredDiscounts.
    couponId: z.string().min(1).optional(),
    // Ordered desired Discount Stack written on create (Bundle Discount #0, then
    // operator coupons). On create every entry is a {coupon} (no discount to reuse).
    desiredDiscounts: z.array(StripeDesiredDiscountV1Schema).optional(),
    prorationBehavior: StripeProrationBehaviorSchema.optional(),
  }),
  z.object({
    kind: z.literal("subscription.update"),
    subscriptionId: z.string().min(1),
    expectedWebhookKinds: z.array(z.string().min(1)).min(1),
    update: z
      .object({
        cancelAtPeriodEnd: z.boolean().optional(),
        // Unix seconds; Stripe `cancel_at`. Schedules the cancellation for a
        // specific future moment (e.g. a commitment boundary) instead of the
        // current period end.
        cancelAt: z.number().optional(),
        itemOperations: z
          .array(StripeProviderMutationItemOperationV1Schema)
          .min(1)
          .optional(),
        // Deprecated single coupon; superseded by desiredDiscounts.
        couponId: z.string().min(1).nullable().optional(),
        // Ordered desired Discount Stack: undefined = no discount change;
        // [] = clear; [...] = set exactly (bundle #0 then operator coupons).
        desiredDiscounts: z.array(StripeDesiredDiscountV1Schema).optional(),
        prorationBehavior: StripeProrationBehaviorSchema.optional(),
        trialEndNow: z.boolean().optional(),
      })
      .refine(
        (value) =>
          value.cancelAtPeriodEnd !== undefined ||
          value.cancelAt !== undefined ||
          value.itemOperations !== undefined ||
          value.couponId !== undefined ||
          value.desiredDiscounts !== undefined ||
          value.prorationBehavior !== undefined ||
          value.trialEndNow !== undefined,
        "subscription.update requires at least one update field"
      ),
  }),
  z.object({
    kind: z.literal("subscription_schedule.update"),
    scheduleId: z.string().min(1),
    subscriptionId: z.string().min(1),
    expectedWebhookKinds: z.array(z.string().min(1)).min(1),
    update: z
      .object({
        cancelAtPeriodEnd: z.boolean().optional(),
        itemOperations: z
          .array(StripeProviderMutationItemOperationV1Schema)
          .min(1)
          .optional(),
        // Deprecated single coupon; superseded by desiredDiscounts.
        couponId: z.string().min(1).nullable().optional(),
        // Ordered desired Discount Stack: undefined = no discount change;
        // [] = clear; [...] = set exactly (bundle #0 then operator coupons).
        desiredDiscounts: z.array(StripeDesiredDiscountV1Schema).optional(),
        prorationBehavior: StripeProrationBehaviorSchema.optional(),
        quantity: z.number().int().nonnegative().optional(),
      })
      .refine(
        (value) =>
          value.cancelAtPeriodEnd !== undefined ||
          value.itemOperations !== undefined ||
          value.couponId !== undefined ||
          value.desiredDiscounts !== undefined ||
          value.prorationBehavior !== undefined,
        "subscription_schedule.update requires at least one update field"
      ),
  }),
]);

export type StripeProviderMutationV1 = z.infer<
  typeof StripeProviderMutationV1Schema
>;

export const MutationSummarySchema = z.object({
  mutationKind: z.string().min(1),
  expectedWebhookKinds: z.array(z.string().min(1)).min(1),
});

export type MutationSummary = z.infer<typeof MutationSummarySchema>;

export const ProviderExecutionStatusList = [
  "succeeded",
  "failed_retryable",
  "failed_non_retryable",
  "webhook_timeout",
  "requires_customer_action",
  "superseded",
] as const;

export type ProviderExecutionStatus =
  (typeof ProviderExecutionStatusList)[number];

export const ProviderExecutionStatusSchema = z.enum(
  ProviderExecutionStatusList
);

export const WaitOutcomeList = [
  "webhook_confirmed",
  "webhook_timeout",
  "webhook_rejected",
  "execution_failed",
  "customer_ready_timeout",
  // Synthesized by stripe-delayed-create when post-wait Stripe validation says
  // the customer is still not billing-ready. Routes the SF back to a fresh
  // stripeCustomerReadyWait state (own task token) instead of rebinding the
  // delayed-create token, which would skip the actual mutation on resume.
  "customer_ready_re_wait",
] as const;

export type WaitOutcome = (typeof WaitOutcomeList)[number];

export const WaitOutcomeSchema = z.enum(WaitOutcomeList);

// SFN catch error metadata carried on resume payloads when waitOutcome is
// execution_failed. Optional on the schema so existing webhook resume payloads
// (webhook_confirmed/webhook_rejected/webhook_timeout) parse unchanged.
export const FailureContextSchema = z.object({
  errorName: z.string(),
  cause: z.string().optional(),
});

export type FailureContext = z.infer<typeof FailureContextSchema>;

// strict() guarantees clientSecret cannot be smuggled in via this contract;
// 3DS/SCA UI must fetch the clientSecret from the dedicated endpoint instead.
export const RequiresCustomerActionContextSchema = z
  .object({
    paymentIntentId: z.string().min(1),
    nextActionType: z.string().min(1).optional(),
    hostedInvoiceUrl: z.string().min(1).optional(),
    invoiceId: z.string().min(1).optional(),
    paymentMethodId: z.string().min(1).optional(),
  })
  .strict();

export type RequiresCustomerActionContext = z.infer<
  typeof RequiresCustomerActionContextSchema
>;

const StripeChildInputVariantSchema = z.object({
  schemaVersion: z.literal("v1"),
  billingProvider: z.literal("stripe"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  executionId: z.string().min(1),
  idempotencyKeys: IdempotencyKeysSchema,
  executionPlanVersion: z.string().min(1),
  existingSubscriptionRefs: ExistingSubscriptionRefsSchema,
  providerInputs: StripeProviderExecutionInputsSchema.optional(),
  providerMutation: StripeProviderMutationV1Schema.nullish(),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
});

/**
 * How the provider-execution state machine routes a Shopify plan.
 * `reactive_plan_apply` answers a cap approval against the live subscription
 * and can never convert a legacy bridge; `bridge_conversion` sends the plan
 * through the mutation lane, which prepares the receipt and charge hold a
 * conversion needs. Absent means the trial-create waiter path.
 */
export const ShopifyExecutionModeSchema = z.enum([
  "reactive_plan_apply",
  "bridge_conversion",
]);

export type ShopifyExecutionMode = z.infer<typeof ShopifyExecutionModeSchema>;

const ShopifyChildInputVariantSchema = z.object({
  schemaVersion: z.literal("v1"),
  billingProvider: z.literal("shopify"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  executionId: z.string().min(1),
  idempotencyKeys: IdempotencyKeysSchema,
  executionPlanVersion: z.string().min(1),
  shopifyExecutionMode: ShopifyExecutionModeSchema.optional(),
  existingSubscriptionRefs: ExistingSubscriptionRefsSchema,
  existingShopifyRefs: ShopifyExistingRefsSchema,
  providerInputs: StripeProviderExecutionInputsSchema.optional(),
  providerMutation: StripeProviderMutationV1Schema.nullish(),
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
});

export const ProviderExecutionChildInputV1Schema = z.discriminatedUnion(
  "billingProvider",
  [StripeChildInputVariantSchema, ShopifyChildInputVariantSchema]
);

export type ProviderExecutionChildInputV1 = z.infer<
  typeof ProviderExecutionChildInputV1Schema
>;

export type StripeProviderExecutionChildInputV1 = Extract<
  ProviderExecutionChildInputV1,
  { billingProvider: "stripe" }
>;

export type ShopifyProviderExecutionChildInputV1 = Extract<
  ProviderExecutionChildInputV1,
  { billingProvider: "shopify" }
>;

export const StripePendingMutationV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  executionId: z.string().min(1),
  taskToken: z.string().min(1),
  stripeIdempotencyKey: z.string().min(1),
  pendingStripeRefs: StripeSubscriptionRefsSchema,
  mutationSummary: MutationSummarySchema,
});

export type StripePendingMutationV1 = z.infer<
  typeof StripePendingMutationV1Schema
>;

export const StripePreflightDecisionList = [
  "immediate",
  "delayed_customer_ready",
] as const;

export type StripePreflightDecision =
  (typeof StripePreflightDecisionList)[number];

export interface StripePreflightTaskResponse {
  decision: StripePreflightDecision;
  stripeCustomerId?: string;
  reason?: string;
}

export interface StripeCustomerReadyWaitResponse {
  schemaVersion: "v1";
  orgId: string;
  commandId: string;
  executionId: string;
  taskToken: string;
  waitKind: "customer_billing_ready";
  stripeCustomerId: string;
  reason?: string;
}

export interface BillingProviderDelayedCreateTimeoutMarkerPayload {
  markerType: "provider_delayed_create_timeout";
  schemaVersion: "v1";
  orgId: string;
  commandId: string;
  billingProvider: "stripe";
  providerWaitKind: "customer_billing_ready";
  stripeCustomerId: string;
}

export const StripeBillingWorkflowSubscriptionMutationResponseV1Schema =
  z.object({
    id: z.string().min(1),
  });

export type StripeBillingWorkflowSubscriptionMutationResponseV1 = z.infer<
  typeof StripeBillingWorkflowSubscriptionMutationResponseV1Schema
>;

export interface StripeMutationShortCircuitResponse {
  outcome: "waiting_on_webhook";
  stripeRequestRef: string | null;
  webhookCorrelationKey: string;
}

export type StripeMutationTaskResponse =
  | StripePendingMutationV1
  | StripeMutationShortCircuitResponse
  | StripeCustomerReadyWaitResponse;

export interface StripeMutationSubscriptionsAdapter {
  subscriptions: {
    createBillingWorkflowSubscription(
      params: Stripe.SubscriptionCreateParams,
      opts?: { idempotencyKey?: string }
    ): Promise<StripeBillingWorkflowSubscriptionMutationResponseV1>;
    updateBillingWorkflowSubscription(
      id: string,
      params?: Stripe.SubscriptionUpdateParams,
      opts?: { idempotencyKey?: string }
    ): Promise<StripeBillingWorkflowSubscriptionMutationResponseV1>;
    // Module-trial grant (ADR 0013): read billing_mode to decide migrate-on-grant.
    // billing_mode is a newer field absent from the pinned SDK's Subscription
    // type, so it is surfaced as an optional overlay here.
    retrieve(
      id: string,
      params?: Stripe.SubscriptionRetrieveParams
    ): Promise<
      Stripe.Response<Stripe.Subscription> & {
        billing_mode?: { type?: string };
      }
    >;
    // Migrate a classic sub to flexible so a Trial Offer can attach (one-way).
    migrateToFlexible(
      id: string
    ): Promise<Stripe.Response<Stripe.Subscription>>;
  };
  subscriptionSchedules: {
    getSubscriptionScheduleById(
      scheduleId: string
    ): Promise<Stripe.SubscriptionSchedule>;
    update(
      scheduleId: string,
      params?: Stripe.SubscriptionScheduleUpdateParams,
      opts?: { idempotencyKey?: string }
    ): Promise<Stripe.SubscriptionSchedule>;
  };
  // Trial Offers preview client (per-request version override, never the
  // client-wide dahlia pin). Used only for module-trial grants.
  trialOffers: {
    resolveTrialOfferIdForPrice(input: {
      priceId: string;
    }): Promise<string | null>;
    attachTrialOfferToSubscription(input: {
      subscriptionId: string;
      trialOfferId: string;
      trialEnd: number;
      // The volume-tier quantity (order-count metric) the item must carry so it
      // converts at the correct tier at trial end, not Stripe's default of 1.
      quantity?: number;
      idempotencyKey?: string;
    }): Promise<{ subscriptionItemId: string }>;
  };
}

// executionId is intentionally omitted: webhook ingress runs in its own SFN
// execution and cannot observe the runtime SM's $$.Execution.Id; the finalize
// handler reads executionId from the preserved childInput instead.
export const StripeWebhookResumeV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  waitOutcome: WaitOutcomeSchema,
  finalStripeRefs: StripeSubscriptionRefsSchema,
  observedWebhookKind: z.string().min(1).optional(),
  observedAt: z.string().min(1),
  previousAttributes: z.record(z.string(), z.unknown()).optional(),
  currentAttributes: z.record(z.string(), z.unknown()).optional(),
  failureContext: FailureContextSchema.optional(),
});

export type StripeWebhookResumeV1 = z.infer<typeof StripeWebhookResumeV1Schema>;

export const ShopifyPendingApprovalV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  executionId: z.string().min(1),
  taskToken: z.string().min(1),
  shopifyChargeKey: z.string().min(1),
  approvalCorrelationKey: z.string().min(1),
  pendingShopifyRefs: ShopifyExistingRefsSchema,
  mutationSummary: MutationSummarySchema,
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
});

export type ShopifyPendingApprovalV1 = z.infer<
  typeof ShopifyPendingApprovalV1Schema
>;

// approvalCorrelationKey is OPTIONAL because synthesized timeout resumes
// (issue #12) have no source for it; finalize falls back to a commandId-based
// correlation lookup. Real webhook resumes look it up via
// shopify_request_correlation by shopifyChargeId (issue #10).
export const ShopifyApprovalResumeV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  waitOutcome: WaitOutcomeSchema,
  finalShopifyRefs: ShopifyExistingRefsSchema,
  observedAt: z.string().min(1),
  approvalCorrelationKey: z.string().min(1).optional(),
  approvalPayload: z.record(z.string(), z.unknown()).optional(),
  failureContext: FailureContextSchema.optional(),
});

export type ShopifyApprovalResumeV1 = z.infer<
  typeof ShopifyApprovalResumeV1Schema
>;

// Shopify approval-wait ladder.
//
// Every waitForTaskToken timeout on ShopifyMutationTask is drawn from this
// ladder: the CDK definition seeds attempt 1, the reconcile-probe service
// computes attempt N+1 and decides exhaustion against the cumulative cap.
// Both sides read these values so neither can drift a rung out of step with
// the other.
//
// Attempt 1 is deliberately longer than the rungs that follow. The invocation
// that parks on the task token is the same one that performs the remote
// Shopify create and then commits the ownership correlation, and the re-entry
// short circuit keys on exactly that correlation. A timeout that expired
// mid-dispatch would let the probe loop back into ShopifyMutationTask while
// the first invocation was still creating, the second invocation would see no
// correlation, and the merchant would be issued two charges. Dispatch is
// bounded by the provider task Lambda timeout, so attempt 1 waits that
// ceiling plus the normal first probe interval, which puts a mid-dispatch
// timeout out of reach.
export const BILLING_PROVIDER_TASK_TIMEOUT_SECONDS = 300;

export const SHOPIFY_APPROVAL_PROBE_LADDER_SECONDS = [
  15, 45, 120, 300, 600, 1800,
] as const;

export const SHOPIFY_APPROVAL_PROBE_PLATEAU_SECONDS = 1800;

// The pre-loop approval window: once the ladder has parked this long in
// aggregate the wait settles via the webhook_timeout path.
export const SHOPIFY_APPROVAL_PROBE_CUMULATIVE_CAP_SECONDS = 24 * 60 * 60;

export const SHOPIFY_APPROVAL_FIRST_WAIT_SECONDS =
  BILLING_PROVIDER_TASK_TIMEOUT_SECONDS +
  SHOPIFY_APPROVAL_PROBE_LADDER_SECONDS[0];

export function shopifyApprovalWaitSecondsForAttempt(attempt: number): number {
  if (attempt <= 1) {
    return SHOPIFY_APPROVAL_FIRST_WAIT_SECONDS;
  }
  return (
    SHOPIFY_APPROVAL_PROBE_LADDER_SECONDS[attempt - 1] ??
    SHOPIFY_APPROVAL_PROBE_PLATEAU_SECONDS
  );
}

export function cumulativeShopifyApprovalWaitSeconds(attempts: number): number {
  let total = 0;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    total += shopifyApprovalWaitSecondsForAttempt(attempt);
  }
  return total;
}

const StripeResultVariantSchema = z.object({
  schemaVersion: z.literal("v1"),
  billingProvider: z.literal("stripe"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  executionId: z.string().min(1),
  providerExecutionStatus: ProviderExecutionStatusSchema,
  finalStripeRefs: StripeSubscriptionRefsSchema.optional(),
  retryable: z.boolean().optional(),
  errorCode: z.string().min(1).optional(),
  failureContext: FailureContextSchema.optional(),
  actionContext: RequiresCustomerActionContextSchema.optional(),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
});

const ShopifyResultVariantSchema = z.object({
  schemaVersion: z.literal("v1"),
  billingProvider: z.literal("shopify"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  executionId: z.string().min(1),
  providerExecutionStatus: ProviderExecutionStatusSchema,
  finalStripeRefs: StripeSubscriptionRefsSchema.optional(),
  finalShopifyRefs: ShopifyBillingRefsSchema.optional(),
  retryable: z.boolean().optional(),
  errorCode: z.string().min(1).optional(),
  failureContext: FailureContextSchema.optional(),
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  // Present when this execution left a bridge mutation receipt parked awaiting
  // an answer Shopify never gave, so the settled command's error details name
  // the receipt targeted repair must resolve instead of leaving an operator to
  // reconstruct it.
  bridgeMutationRecoveryRef: BridgeMutationRecoveryRefV1Schema.optional(),
  workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
});

export const ProviderExecutionResultV1Schema = z.discriminatedUnion(
  "billingProvider",
  [StripeResultVariantSchema, ShopifyResultVariantSchema]
);

export type ProviderExecutionResultV1 = z.infer<
  typeof ProviderExecutionResultV1Schema
>;

export type StripeProviderExecutionResultV1 = Extract<
  ProviderExecutionResultV1,
  { billingProvider: "stripe" }
>;

export type ShopifyProviderExecutionResultV1 = Extract<
  ProviderExecutionResultV1,
  { billingProvider: "shopify" }
>;

export const ProviderOutcomeGateRouteList = [
  "succeeded",
  "skip_terminal",
] as const;

export type ProviderOutcomeGateRoute =
  (typeof ProviderOutcomeGateRouteList)[number];

export const ProviderOutcomeGateOutputV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  route: z.enum(ProviderOutcomeGateRouteList),
  providerExecutionStatus: ProviderExecutionStatusSchema,
  errorCode: z.string().min(1).optional(),
});

export type ProviderOutcomeGateOutputV1 = z.infer<
  typeof ProviderOutcomeGateOutputV1Schema
>;

export function assertProviderOutcomeGateOutput(
  value: unknown
): asserts value is ProviderOutcomeGateOutputV1 {
  const parsed = ProviderOutcomeGateOutputV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "provider outcome gate output is missing required fields",
      parsed.error
    );
  }
}
